"use client";

import { Fragment, MouseEvent } from "react";
import { Icon, IconName } from "~/components/icons/Icon";
import Button from "~/components/buttons/Button";
import Flex from "~/components/flex/Flex";
import HorizontalLine from "~/components/horizontalLine/HorizontalLine";
import Modal from "~/components/modal/Modal";
import Switch from "~/components/switch/NewSwitch";
import Text from "~/components/texts/Text";
import { useI18n } from "~/locales/client";
import { CookieSettingType, CookieTypeEnum } from "~/types-and-enums/cookieTypes";
import { ButtonTypesEnum } from "~/types-and-enums/generalTypes";
import { cookieSettings } from "~/utils/cookieUtils";
import { ColorModeEnum } from "~/utils/generalUtils";
import { colors, spacing } from "~/utils/tailwindUtils";

type CookiePreferencesProps = {
  isModalOpen: boolean;
  onClose: () => void;
  handleCookieSettingClick: (setting: CookieSettingType) => void;
  handleConfirm: () => void;
  selectedCookies: CookieTypeEnum[];
};

/**
 * CookiePreferences component that allows users to manage their cookie preferences.
 * @param isModalOpen - boolean to determine if the modal is open
 * @param onClose - function to close the modal
 * @param handleCookieSettingClick - function to handle the cookie setting click
 * @param handleConfirm - function to handle the confirm button click
 * @param selectedCookies - array of selected cookies
 */
const CookiePreferences = ({
  isModalOpen,
  onClose,
  handleCookieSettingClick,
  handleConfirm,
  selectedCookies,
}: CookiePreferencesProps) => {
  const translate = useI18n();

  return (
    <Modal
      isOpen={isModalOpen}
      closeModal={onClose}
      size="sm"
      classname="w-full md:w-[60vw] pt-0 pb-6 md:pt-0 lg:pt-0"
      childrenDivWrapperClassName="gap-4 md:gap-8"
    >
      <Flex className="flex-col gap-4">
        <Flex className="rounded-full hidden md:flex bg-gray-75 p-2 w-fit mx-auto">
          <Icon
            name={IconName.CookiesPreferences}
            width={spacing[12]}
            height={spacing[12]}
            color={colors.gray[200]}
            colorMode={ColorModeEnum.Fill}
          />
        </Flex>
        <Text variant="h2" className="text-center w-full">
          {translate("cookieBanner.modal.title")}
        </Text>
        <Text className="text-center">{translate("cookieBanner.modal.description")}</Text>
      </Flex>
      <Flex className="flex-col gap-4">
        {cookieSettings.map((setting, index) => (
          <Fragment key={setting.type}>
            {index > 0 && <HorizontalLine className="w-full" />}
            <Flex
              key={setting.type}
              className={`flex-col ${
                setting.type === CookieTypeEnum.Functional ? "cursor-not-allowed" : "cursor-pointer"
              }`}
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                handleCookieSettingClick(setting);
              }}
            >
              <Flex className="items-center justify-between w-full gap-2 mb-2">
                <Text fontWeight="semibold" className="text-left">
                  {translate(setting.title)}
                </Text>
                <Switch checked={selectedCookies.includes(setting.type)} disabled={setting.required} />
              </Flex>
              <Text color="gray" variant="span">
                {translate(setting.description)}
              </Text>
            </Flex>
          </Fragment>
        ))}
      </Flex>
      <Flex className="gap-2 w-full justify-between flex-col md:flex-row">
        <Button
          buttonStyle={ButtonTypesEnum.default}
          onClick={onClose}
          text={translate("general.cancel")}
          buttonClassName="bg-white border-gray-300 w-full md:w-fit hover:bg-gray-50"
          textClassName="text-gray-900"
        />
        <Button
          onClick={handleConfirm}
          text={translate("general.confirm-preferences")}
          buttonClassName="px-4 w-full md:w-fit"
        />
      </Flex>
    </Modal>
  );
};

export default CookiePreferences;
